import { zodResolver } from '@hookform/resolvers/zod';
import type { Resolver } from 'react-hook-form';
import { z } from 'zod';

/**
 * What a notice must look like before the office can put it up.
 *
 * Mirrors the backend's `instituteNotices.validation.ts` — the server is the
 * authority, these rules only save a round trip. Keep the two in step.
 *
 * The one rule deliberately left to the server is whether the date falls inside
 * the academic year: the session's own start and end are not in the browser, and
 * guessing them here would reject a valid date on an institute whose year runs
 * differently.
 */

export const NOTICE_AUDIENCES = ['EVERYONE', 'STAFF', 'STUDENTS'] as const;

/** Every kind a notice on the board can carry — the class board's vocabulary. */
export const NOTICE_KINDS = ['GENERAL', 'EVENT', 'REMINDER'] as const;

/**
 * What the office may choose for a *new* notice: General only. Event and
 * Reminder are no longer offered, but notices already on the board keep their
 * kind, so both stay in `NOTICE_KINDS`.
 */
export const POSTABLE_NOTICE_KINDS = ['GENERAL'] as const;

export type PostableNoticeKind = (typeof POSTABLE_NOTICE_KINDS)[number];

/**
 * What the *institute* board may carry — three kinds, and deliberately still
 * three.
 *
 * Not the teacher board's `NoticeType`, which also has Academics and Important:
 * those are a teacher saying something about their own subject or their own
 * class, and mean nothing on the office's broadcast. The two columns are two
 * enums in the database for the same reason.
 */
export type InstituteNoticeKind = (typeof NOTICE_KINDS)[number];

/** The `YYYY-MM-DD` an `<input type="date">` holds, and the API takes. */
const day = z
  .string()
  .trim()
  .min(1, 'Pick a date')
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-15');

export const instituteNoticeSchema = z.object({
  audience: z.enum(NOTICE_AUDIENCES, {
    errorMap: () => ({ message: 'Choose who this notice is for' }),
  }),
  noticeType: z.enum(POSTABLE_NOTICE_KINDS, {
    errorMap: () => ({ message: 'Choose what kind of notice this is' }),
  }),
  title: z
    .string()
    .trim()
    .min(3, 'Give the notice a title')
    .max(150, 'That title is too long'),
  body: z
    .string()
    .trim()
    .max(2000, 'Keep the details under 2000 characters')
    // Blank is the ordinary case — most notices are a single line — and the API
    // takes the field absent rather than empty.
    .transform((value) => value || undefined),
  noticeDate: day,
});

/** What the form holds while it is being filled in — every field a string. */
export interface InstituteNoticeFormValues {
  audience: (typeof NOTICE_AUDIENCES)[number];
  noticeType: PostableNoticeKind;
  title: string;
  body: string;
  noticeDate: string;
}

/**
 * The resolver the compose form uses.
 *
 * `raw` keeps the form holding what the inputs hold. Without it the resolver
 * hands the submit handler the *transformed* values — an empty body already
 * `undefined` — and the `parse()` that builds the payload then rejects it as a
 * missing string, so posting a one-line notice would always fail. The holidays
 * form carries the same note for the same reason.
 */
export const instituteNoticeFormResolver: Resolver<InstituteNoticeFormValues> = zodResolver(
  instituteNoticeSchema,
  undefined,
  { raw: true },
);

/**
 * A blank notice: for everybody, general, dated today.
 *
 * Today rather than empty because a notice put up now is nearly always about
 * now, and the office should have to change the date only when it is not.
 */
export function emptyInstituteNoticeForm(today: string): InstituteNoticeFormValues {
  return {
    audience: 'EVERYONE',
    noticeType: 'GENERAL',
    title: '',
    body: '',
    noticeDate: today,
  };
}

/**
 * The form refilled from a notice already up, for editing it.
 *
 * An older Event or Reminder comes back as General: General is the only kind
 * the office posts now.
 */
export function instituteNoticeFormFrom(notice: {
  audience: InstituteNoticeFormValues['audience'];
  noticeType: InstituteNoticeKind;
  title: string;
  body: string;
  noticeDate: string;
}): InstituteNoticeFormValues {
  return {
    audience: notice.audience,
    noticeType: 'GENERAL',
    title: notice.title,
    body: notice.body,
    noticeDate: notice.noticeDate,
  };
}
